import { apiFetch, authHeader, ApiError } from '../../../shared/api/httpClient';
import { clientHeaders } from '../../../shared/lib/device';

export type FeedbackStatus = 'pending' | 'replied' | 'closed';

// Field names mirror FeedbackController's JSON verbatim (snake_case, epoch
// seconds) rather than being remapped to camelCase — every other api.ts in
// this codebase passes server shapes through unchanged, so later tasks that
// import these types can rely on that.
export type FeedbackItem = {
  id: number;
  content: string;
  status: FeedbackStatus;
  reply_content: string;
  replied_at: number | null;
  read_at: number | null;
  created_at: number;
  image_count: number;
};

export type FeedbackDetail = FeedbackItem & {
  images: Array<{ id: number; content_type: string }>;
};

/**
 * POST /api/feedback (multipart). Uses raw `fetch` for historical reasons —
 * apiFetch now leaves a FormData body's Content-Type to the browser (see
 * defaultContentType in httpClient.ts, since commit eb4d7e73), so it would
 * work here too and this no longer needs to bypass it.
 */
export async function submitFeedback(
  token: string,
  content: string,
  images: Blob[],
): Promise<{ id: number }> {
  const form = new FormData();
  form.append('content', content);
  images.forEach((blob) => form.append('images', blob, 'screenshot.jpg'));

  const response = await fetch('/api/feedback', {
    method: 'POST',
    body: form,
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const code = payload && typeof payload === 'object' && 'code' in payload ? String(payload.code) : '';
    throw new ApiError(code || `HTTP_${response.status}`, response.status, payload);
  }
  return payload as { id: number };
}

/**
 * GET /api/feedback. `before` is a strictly-exclusive cursor: the server
 * binds it as `f.id < before` (see FeedbackController#list), so passing the
 * last-loaded item's id returns the next older page. Omit it (or pass a
 * value it can't parse as a long) to get the first page — the server treats
 * both the same way, never as an error.
 */
export function fetchFeedbackList(
  token: string,
  before?: number,
): Promise<{ items: FeedbackItem[]; unread: number }> {
  const query = before != null ? `?before=${encodeURIComponent(String(before))}` : '';
  return apiFetch(`/api/feedback${query}`, {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

/**
 * GET /api/feedback/{id}. Pure read — the server runs this handler in a
 * read-only transaction and never marks anything read here. Call
 * markFeedbackRead separately once the reply has actually been shown.
 */
export function fetchFeedbackDetail(token: string, id: number): Promise<FeedbackDetail> {
  return apiFetch(`/api/feedback/${encodeURIComponent(String(id))}`, {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

/**
 * POST /api/feedback/{id}/read. Kept separate from fetchFeedbackDetail on
 * purpose: the detail GET runs read-only and cannot perform this write.
 */
export async function markFeedbackRead(token: string, id: number): Promise<void> {
  await apiFetch<{ ok: boolean; id: number }>(`/api/feedback/${encodeURIComponent(String(id))}/read`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

export function fetchFeedbackSummary(token: string): Promise<{ unread: number }> {
  return apiFetch('/api/feedback/summary', {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

/**
 * GET /api/feedback/images/{id}. This endpoint requires the auth header, so
 * a plain <img src> can't load it directly — fetch the bytes through the
 * authenticated path and hand back an object URL instead.
 *
 * Caller MUST call URL.revokeObjectURL() on the returned string once done
 * with it (e.g. on unmount): each call here allocates a new blob URL, and
 * leaving them un-revoked leaks memory across the browsing session.
 */
export async function fetchFeedbackImageUrl(token: string, imageId: number): Promise<string> {
  const response = await fetch(`/api/feedback/images/${encodeURIComponent(String(imageId))}`, {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
  if (!response.ok) {
    throw new ApiError(`HTTP_${response.status}`, response.status, null);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}
